import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MapPinOff } from 'lucide-react';
import { MAPBOX_TOKEN, MAP_STYLE } from '../lib/mapConfig';

mapboxgl.accessToken = MAPBOX_TOKEN;

const ZONE_COLOR = '#4A7C59';
const NO_ZONE = [];
const ZONE_MIN_RADIUS_M = 60;
const METERS_PER_DEG_LAT = 111320;

const circle = ([lng, lat], radiusM, steps = 48) => {
  const dLat = radiusM / METERS_PER_DEG_LAT;
  const dLng = radiusM / (METERS_PER_DEG_LAT * Math.cos((lat * Math.PI) / 180));
  const ring = Array.from({ length: steps }, (_, i) => {
    const a = (i / steps) * 2 * Math.PI;
    return [lng + dLng * Math.cos(a), lat + dLat * Math.sin(a)];
  });
  return [...ring, ring[0]];
};

// Zona morbida: poligono dei punti, oppure un cerchio quando i punti sono uno o due
const zoneRing = (zone) => {
  if (zone.length >= 3) return [...zone, zone[0]];
  const center = [zone.reduce((s, p) => s + p[0], 0) / zone.length, zone.reduce((s, p) => s + p[1], 0) / zone.length];
  const [a, b] = [zone[0], zone[zone.length - 1]];
  const halfSpan = Math.hypot(
    (b[0] - a[0]) * METERS_PER_DEG_LAT * Math.cos((center[1] * Math.PI) / 180),
    (b[1] - a[1]) * METERS_PER_DEG_LAT
  ) / 2;
  return circle(center, Math.max(ZONE_MIN_RADIUS_M, halfSpan + 40));
};

const pinElement = (pin) => {
  const el = document.createElement('div');
  el.className = 'flex flex-col items-center';
  el.setAttribute('data-testid', `walk-map-pin-${pin.label ? pin.label.toLowerCase() : 'punto'}`);
  const tag = document.createElement('span');
  tag.className = `mb-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold shadow ${
    pin.label ? 'bg-white text-[#2C3E50]' : 'bg-white/80 text-[#64748B]'
  }`;
  tag.textContent = pin.caption;
  const dot = document.createElement('span');
  dot.className = `block rounded-full border-2 border-white shadow ${
    pin.label === 'Rientro' ? 'h-4 w-4 bg-[#EF4444]' : pin.label === 'Partenza' ? 'h-4 w-4 bg-[#4A7C59]' : 'h-3 w-3 bg-[#94A3B8]'
  }`;
  el.append(tag, dot);
  return el;
};

export const WalkMap = ({ pins, zone = NO_ZONE, testId = 'walk-map' }) => {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!pins.length || !containerRef.current) return undefined;
    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: MAP_STYLE,
      center: [pins[0].lng, pins[0].lat],
      zoom: 15,
      cooperativeGestures: true,
      attributionControl: false
    });
    map.addControl(new mapboxgl.AttributionControl({ compact: true }));
    pins.forEach((pin) => new mapboxgl.Marker({ element: pinElement(pin), anchor: 'bottom' }).setLngLat([pin.lng, pin.lat]).addTo(map));

    const ring = zone.length ? zoneRing(zone) : null;
    map.on('load', () => {
      if (!ring) return;
      map.addSource('walk-zone', { type: 'geojson', data: { type: 'Feature', geometry: { type: 'Polygon', coordinates: [ring] } } });
      map.addLayer({ id: 'walk-zone-fill', type: 'fill', source: 'walk-zone', paint: { 'fill-color': ZONE_COLOR, 'fill-opacity': 0.14 } });
      map.addLayer({
        id: 'walk-zone-edge', type: 'line', source: 'walk-zone',
        paint: { 'line-color': ZONE_COLOR, 'line-width': 22, 'line-blur': 18, 'line-opacity': 0.22 }
      });
    });

    const bounds = new mapboxgl.LngLatBounds();
    [...pins.map((p) => [p.lng, p.lat]), ...(ring || [])].forEach((c) => bounds.extend(c));
    if (pins.length > 1 || (ring && zone.length > 1)) map.fitBounds(bounds, { padding: 48, maxZoom: 16, duration: 0 });
    return () => map.remove();
  }, [pins, zone]);

  if (!pins.length) {
    return (
      <div className="flex items-center gap-2 rounded-xl bg-[#F8FAFC] px-4 py-3 text-sm text-[#64748B]" data-testid={`${testId}-unavailable`}>
        <MapPinOff className="w-4 h-4" />
        Posizione non disponibile
      </div>
    );
  }
  return <div ref={containerRef} className="h-56 w-full overflow-hidden rounded-xl" data-testid={testId} />;
};

export const placesText = (pins) => {
  const start = pins.find((p) => p.label === 'Partenza')?.place;
  const end = pins.find((p) => p.label === 'Rientro')?.place;
  if (start && end) return `Partiti da ${start}, rientrati da ${end}`;
  if (start) return `Partiti da ${start}`;
  if (end) return `Rientrati da ${end}`;
  return null;
};
